import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, IconButton, Alert, Typography, Tooltip } from '@mui/material';
import {
  SmartToy,
  Refresh,
  ArticleOutlined,
  Fullscreen,
  FullscreenExit
} from '@mui/icons-material';
import SidebarPanelHeader from './SidebarPanelHeader';
import MessagesList from './AIAssistant/MessagesList';
import ChatInputField from './AIAssistant/ChatInputField';
import { Message, MessageStep, ApprovalRequest } from './AIAssistant/types';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  sendGTMEngineerMessageStream,
  sendGTMEngineerApprovalStream,
  GTMEngineerStepEvent,
  GTMEngineerApprovalRequiredPayload
} from '../../api/gtmEngineer';
import AgentMemoryEditorDialog from './AIAssistant/AgentMemoryEditorDialog';

function parseApprovalRequest(
  payload: GTMEngineerApprovalRequiredPayload
): ApprovalRequest {
  return { display_message: payload.display_message };
}

/** Stack approval payloads so multiple `approval_required` events show as one batched UI. */
function mergeApprovalRequests(
  existing: ApprovalRequest[] | undefined,
  payload: GTMEngineerApprovalRequiredPayload
): ApprovalRequest[] {
  return [...(existing ?? []), parseApprovalRequest(payload)];
}

interface AIAssistantPanelProps {
  onClose: () => void;
  /** When GTM engineer stream completes successfully (no pending approval), refresh sheet from backend. */
  onGtmEngineerComplete?: () => void;
  /** Full-width layout: campaign page hides the spreadsheet. */
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const AIAssistantPanel: React.FC<AIAssistantPanelProps> = ({
  onClose,
  onGtmEngineerComplete,
  isExpanded = false,
  onExpandedChange
}) => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [chatSessionId, setChatSessionId] = useState<string | null>(null);
  const [approvingMessageId, setApprovingMessageId] = useState<string | null>(
    null
  );
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [memoryEditorOpen, setMemoryEditorOpen] = useState(false);

  const chatInputRef = useRef<HTMLTextAreaElement | null>(null);
  const inputLockedRef = useRef(false);

  const hasConversation = messages.length > 0;
  const inputLocked = isLoading || !!approvingMessageId;

  useEffect(() => {
    const wasLocked = inputLockedRef.current;
    if (wasLocked && !inputLocked) {
      const id = requestAnimationFrame(() => {
        chatInputRef.current?.focus();
      });
      inputLockedRef.current = inputLocked;
      return () => cancelAnimationFrame(id);
    }
    inputLockedRef.current = inputLocked;
  }, [inputLocked]);

  const toMessageStep = useCallback((e: GTMEngineerStepEvent): MessageStep => {
    switch (e.kind) {
      case 'tool_call':
        return { kind: 'tool_call', tool: e.tool, message: e.display_message };
      case 'message':
        return { kind: 'message', content: e.display_message };
      case 'tool_output':
        return { kind: 'tool_output', tool: e.tool, output: e.output };
      default:
        return e as MessageStep;
    }
  }, []);

  const handleSend = () => {
    if (inputValue.trim() && !isLoading && !approvingMessageId && campaignId) {
      setApprovalError(null);
      const userMessage: Message = {
        id: Date.now().toString(),
        text: inputValue.trim(),
        isUser: true
      };

      const messageText = inputValue.trim();
      setMessages((prev) => [...prev, userMessage]);
      setInputValue('');
      setIsLoading(true);

      const aiMessageId = (Date.now() + 1).toString();
      const aiMessage: Message = {
        id: aiMessageId,
        text: '',
        isUser: false
      };

      setMessages((prev) => [...prev, aiMessage]);

      sendGTMEngineerMessageStream(
        messageText,
        campaignId,
        chatSessionId,
        setChatSessionId,
        (step) => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === aiMessageId
                ? { ...msg, steps: [...(msg.steps ?? []), toMessageStep(step)] }
                : msg
            )
          );
        },
        (payload) => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === aiMessageId
                ? {
                    ...msg,
                    approvalRequests: mergeApprovalRequests(
                      msg.approvalRequests,
                      payload
                    ),
                    approvalStatus: 'pending' as const
                  }
                : msg
            )
          );
        },
        (content, approvalPending) => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === aiMessageId
                ? { ...msg, text: content ?? msg.text ?? '' }
                : msg
            )
          );
          if (approvalPending) setIsLoading(false);
        },
        (error: string) => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === aiMessageId ? { ...msg, text: `Error: ${error}` } : msg
            )
          );
          setIsLoading(false);
        },
        () => {
          setIsLoading(false);
          onGtmEngineerComplete?.();
        }
      );
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleApproval = (messageId: string, approved: boolean) => {
    if (!campaignId || !chatSessionId) {
      setApprovalError('Missing session. Please start a new chat.');
      return;
    }

    const msg = messages.find((m) => m.id === messageId);
    if (!msg?.approvalRequests?.length) return;

    setApprovalError(null);
    setApprovingMessageId(messageId);
    setIsLoading(true);

    const continuationMessageId = `continuation-${Date.now()}`;
    const continuationMessage: Message = {
      id: continuationMessageId,
      text: '',
      isUser: false
    };

    setMessages((prev) => {
      const updated = prev.map((m) =>
        m.id === messageId
          ? {
              ...m,
              approvalStatus: (approved ? 'approved' : 'rejected') as
                | 'approved'
                | 'rejected'
            }
          : m
      );
      return [...updated, continuationMessage];
    });

    sendGTMEngineerApprovalStream(
      campaignId,
      chatSessionId,
      approved,
      () => {},
      (step) => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === continuationMessageId
              ? { ...m, steps: [...(m.steps ?? []), toMessageStep(step)] }
              : m
          )
        );
      },
      (payload) => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === continuationMessageId
              ? {
                  ...m,
                  approvalRequests: mergeApprovalRequests(
                    m.approvalRequests,
                    payload
                  ),
                  approvalStatus: 'pending' as const
                }
              : m
          )
        );
      },
      (content) => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === continuationMessageId
              ? { ...m, text: content ?? m.text ?? '' }
              : m
          )
        );
      },
      (error: string) => {
        setApprovalError(error);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === continuationMessageId
              ? { ...m, text: `Error: ${error}` }
              : m
          )
        );
        setApprovingMessageId(null);
        setIsLoading(false);
      },
      () => {
        setApprovingMessageId(null);
        setIsLoading(false);
        onGtmEngineerComplete?.();
      }
    );
  };

  const handleRefresh = () => {
    setMessages([]);
    setInputValue('');
    setIsLoading(false);
    setChatSessionId(null);
    setApprovingMessageId(null);
    setApprovalError(null);
  };

  const handlePanelClose = () => {
    onExpandedChange?.(false);
    onClose();
  };

  const handleToggleExpand = () => {
    onExpandedChange?.(!isExpanded);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden'
      }}
    >
      <SidebarPanelHeader
        icon={<SmartToy />}
        title="AI GTM Engineer"
        actionButtons={[
          <Tooltip key="memory" title="Edit agent memory" arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => setMemoryEditorOpen(true)}
                disabled={!campaignId}
                aria-label="Edit agent memory"
                sx={{ color: 'text.secondary' }}
              >
                <ArticleOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>,
          <IconButton
            key="refresh"
            size="small"
            onClick={handleRefresh}
            aria-label="Reset chat"
            sx={{ color: 'text.secondary' }}
          >
            <Refresh fontSize="small" />
          </IconButton>,
          ...(onExpandedChange
            ? [
                <Tooltip
                  key="expand"
                  title={isExpanded ? 'Exit full screen' : 'Full screen'}
                  arrow
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleToggleExpand}
                      aria-label={
                        isExpanded ? 'Exit full screen' : 'Full screen'
                      }
                      sx={{ color: 'text.secondary' }}
                    >
                      {isExpanded ? (
                        <FullscreenExit fontSize="small" />
                      ) : (
                        <Fullscreen fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              ]
            : []),
          <IconButton
            key="close"
            size="small"
            onClick={handlePanelClose}
            aria-label="Close page"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ]}
      />

      <AgentMemoryEditorDialog
        open={memoryEditorOpen}
        onClose={() => setMemoryEditorOpen(false)}
        campaignId={campaignId}
      />

      {!hasConversation ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <ChatInputField
            ref={chatInputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyPress}
            onSend={handleSend}
            rows={4}
            disabled={inputLocked}
          />
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {approvalError && (
            <Alert
              severity="error"
              onClose={() => setApprovalError(null)}
              sx={{ mx: 1, mb: 1 }}
            >
              <Typography variant="body2">
                {approvalError.toLowerCase().includes('expired')
                  ? 'Approval expired. Please start a new chat or try again.'
                  : approvalError}
              </Typography>
            </Alert>
          )}
          <MessagesList
            messages={messages}
            onApproval={handleApproval}
            isLoading={isLoading}
            approvingMessageId={approvingMessageId}
          />
          <Box sx={{ flexShrink: 0 }}>
            <ChatInputField
              ref={chatInputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              onSend={handleSend}
              rows={2}
              disabled={inputLocked}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AIAssistantPanel;
